import { BooLogo } from "@/components/BooLogo";
import { Lock } from "lucide-react";
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { MockupCard, PreviewShell } from "../previews/PreviewShell";
import {
  CHAT_LABELS,
  firstName,
  formatDayStart,
  initialOf,
  type ChatProvider,
  type CosProfile,
} from "./types";

const BG = "linear-gradient(135deg, #faf7fb 0%, #f0eef5 50%, #f5eef0 100%)";
const DOTS: [string, string, string, string] = ["#D4537E", "#7F77DD", "#534AB7", "#EF9F27"];

// ── Step 1 ──────────────────────────────────────────────────────────────────

export function CosWelcomePreview({ name }: { name: string }) {
  const first = firstName(name);
  return (
    <PreviewShell background={BG} dotColors={DOTS}>
      <div className="flex flex-col items-center gap-6">
        <div className="relative">
          <span
            className="boo-pulse absolute -inset-10 rounded-full"
            style={{
              background:
                "radial-gradient(closest-side, rgba(127, 119, 221, 0.45), transparent 70%)",
            }}
          />
          <span
            className="boo-pulse absolute -inset-14 rounded-full"
            style={{
              background:
                "radial-gradient(closest-side, rgba(212, 83, 126, 0.25), transparent 70%)",
              animationDelay: "400ms",
            }}
          />
          <BooLogo className="relative size-24 rounded-2xl" />
        </div>
        <p className="boo-fade-in text-[13px] text-stone-500" style={{ animationDelay: "600ms" }}>
          {first ? `Chief of staff to ${first}` : "Your chief of staff"}
        </p>
      </div>
    </PreviewShell>
  );
}

// ── Step 2 ──────────────────────────────────────────────────────────────────

const CARDS = [
  { color: "#1D9E75", label: "A briefing before your first meeting" },
  { color: "#534AB7", label: "Replies drafted in your voice" },
  { color: "#D4537E", label: "No double-bookings, travel time included" },
  { color: "#EF9F27", label: "A brief on everyone you're about to meet" },
  { color: "#7F77DD", label: "Talks only to you" },
] as const;

function randomTilt() {
  const sign = Math.random() < 0.5 ? -1 : 1;
  const magnitude = 0.6 + Math.random() * 1.9;
  return `${(sign * magnitude).toFixed(2)}deg`;
}

export function CosCompanyPreview({ profile }: { profile: CosProfile }) {
  const rotations = useMemo(() => CARDS.map(() => randomTilt()), []);
  return (
    <PreviewShell align="start" background={BG} dotColors={DOTS}>
      <div className="flex flex-col items-center gap-6">
        <div className="boo-fade-in relative flex items-center gap-3" style={{ animationDelay: "200ms" }}>
          <span
            className="boo-pulse absolute -inset-6 rounded-full"
            style={{
              background:
                "radial-gradient(closest-side, rgba(127, 119, 221, 0.4), transparent 70%)",
            }}
          />
          <BooLogo className="relative size-20 rounded-2xl" />
        </div>
        <div className="w-1/2 space-y-3">
          {CARDS.map((card, i) => (
            <div
              key={card.label}
              className="boo-spring-in flex items-start gap-3 rounded-xl border border-black/[0.06] bg-white px-5 py-5"
              style={{
                animationDelay: `${i * 90}ms`,
                boxShadow: "0 6px 16px rgba(20, 20, 30, 0.06)",
                ["--card-rot" as string]: rotations[i],
              } as CSSProperties}
            >
              <span className="mt-2 h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ background: card.color }} />
              <span className="text-[16px] font-normal leading-snug text-stone-900">{card.label}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span
            className="boo-fade-in flex h-8 w-8 items-center justify-center rounded-full border-2 border-white text-[10px] font-medium text-white"
            style={{ background: "#534AB7", animationDelay: "600ms" }}
          >
            {initialOf(profile.name)}
          </span>
          <span className="text-[12px] text-stone-500">just you</span>
        </div>
      </div>
    </PreviewShell>
  );
}

// ── Step 3: a day with a chief of staff ─────────────────────────────────────

export function CosSetupPreview({ profile, booName }: { profile: CosProfile; booName: string }) {
  const [showTyping, setShowTyping] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setShowTyping(false), 2600);
    return () => clearTimeout(t);
  }, []);
  const first = firstName(profile.name) || "there";
  const initial = initialOf(profile.name);
  const time = formatDayStart(profile.briefingTime);

  return (
    <PreviewShell>
      <div className="relative w-full">
        <div
          className="boo-fade-in absolute right-0 top-0 z-10 w-[45%] origin-top-right -translate-y-[130%] rotate-3"
          style={{ animationDelay: "1500ms" }}
        >
          <DmSideCard
            booName={booName}
            initial={initial}
            time="11:42 AM"
            userText="reply to Sarah: we're in, but we need until Friday"
            botText="Draft saved. Want to read it first?"
          />
        </div>
        <div
          className="boo-fade-in absolute bottom-0 left-0 z-20 w-[50%] origin-bottom-left translate-y-[130%] -rotate-3"
          style={{ animationDelay: "2000ms" }}
        >
          <DmSideCard
            booName={booName}
            initial={initial}
            time="2:40 PM"
            userText="who's Mark Chen, 3pm?"
            botText="CFO at Northwind, ex-Stripe. You met at SaaStr. Brief attached."
          />
        </div>
        <div className="boo-fade-in relative z-30 -rotate-1" style={{ animationDelay: "200ms" }}>
          <MockupCard>
            <DmHeader booName={booName} />
            <div className="p-4">
              {showTyping && (
                <div className="boo-fade-in flex gap-2.5" style={{ animationDelay: "800ms" }}>
                  <BooLogo className="size-8 flex-shrink-0 rounded-md" />
                  <div className="flex-1">
                    <MsgHeader name={booName} app time={time} big />
                    <TypingDots />
                  </div>
                </div>
              )}
              <div className="boo-fade-in flex gap-2.5" style={{ animationDelay: "2800ms" }}>
                <BooLogo className="size-8 flex-shrink-0 rounded-md" />
                <div className="min-w-0 flex-1">
                  <MsgHeader name={booName} app time={time} big />
                  <div className="space-y-2 text-[13px] leading-snug text-stone-900">
                    <p>Morning, {first}. Four meetings, first at 9:30 with Acme. Brief is ready.</p>
                    <p>Two emails need you: Sarah on the term sheet (draft saved), Priya&apos;s board deck v3.</p>
                    <p>Thursday 2pm overlaps your flight back from Berlin. Move it?</p>
                  </div>
                </div>
              </div>
            </div>
          </MockupCard>
        </div>
      </div>
    </PreviewShell>
  );
}

// ── Step 4: the first DM ────────────────────────────────────────────────────

export function CosChatPreview({
  profile,
  booName,
  inboxConnected,
  chat,
}: {
  profile: CosProfile;
  booName: string;
  inboxConnected: boolean;
  chat: ChatProvider | null;
}) {
  const first = firstName(profile.name) || "there";
  const time = formatDayStart(profile.briefingTime);
  const chips = [
    { color: "#7F77DD", label: "Channels off" },
    { color: "#EF9F27", label: `Briefing ${time}` },
  ];

  return (
    <PreviewShell chips={chips}>
      <div className="boo-fade-in relative -rotate-1" style={{ animationDelay: "200ms" }}>
        <MockupCard>
          <DmHeader booName={booName} platform={chat ? CHAT_LABELS[chat] : undefined} />
          <div className="p-4">
            <div className="flex gap-2.5">
              <BooLogo className="size-8 flex-shrink-0 rounded-md" />
              <div className="min-w-0 flex-1">
                <MsgHeader name={booName} app time="just now" big />
                <div className="space-y-2 text-[13px] leading-snug text-stone-900">
                  <p>Hi {first}. Three quick things before I start:</p>
                  <p>1. Whose email always matters?</p>
                  <p>2. Any dates you never travel?</p>
                  <p>3. How many meetings a day is too many?</p>
                  {!inboxConnected && <p>Then a link to connect your inbox and calendar.</p>}
                  <p className="text-stone-500">Or say &quot;use defaults&quot;.</p>
                </div>
              </div>
            </div>
          </div>
        </MockupCard>
      </div>
    </PreviewShell>
  );
}

// ── Shared mockup bits ──────────────────────────────────────────────────────

function DmHeader({ booName, platform }: { booName: string; platform?: string }) {
  return (
    <div className="flex items-center gap-2 border-b border-stone-200 px-4 py-3">
      <span className="text-[13px] font-medium text-stone-900">direct message · {booName}</span>
      <span className="ml-auto flex items-center gap-1 text-[11px] text-stone-500">
        <Lock className="size-3" />
        {platform ? `Only you · ${platform}` : "Only you"}
      </span>
    </div>
  );
}

function DmSideCard({
  booName,
  initial,
  time,
  userText,
  botText,
}: {
  booName: string;
  initial: string;
  time: string;
  userText: string;
  botText: string;
}) {
  return (
    <MockupCard>
      <div className="flex items-center gap-2 border-b border-stone-200 px-3 py-2">
        <span className="text-[11px] text-stone-500">direct message · {booName}</span>
      </div>
      <div className="space-y-2 px-3 py-2.5">
        <div className="flex gap-2">
          <UserAvatar initial={initial} />
          <div className="min-w-0 flex-1">
            <MsgHeader name="You" time={time} />
            <div className="text-[11px] leading-snug text-stone-900">{userText}</div>
          </div>
        </div>
        <div className="flex gap-2">
          <BooLogo className="size-6 flex-shrink-0 rounded-md" />
          <div className="min-w-0 flex-1">
            <MsgHeader name={booName} app time={time} />
            <div className="text-[11px] leading-snug text-stone-900">{botText}</div>
          </div>
        </div>
      </div>
    </MockupCard>
  );
}

function UserAvatar({ initial }: { initial: string }) {
  return (
    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-indigo-700 text-[9px] font-medium text-white">
      {initial}
    </div>
  );
}

function MsgHeader({
  name,
  time,
  app,
  big,
}: {
  name: ReactNode;
  time: string;
  app?: boolean;
  big?: boolean;
}) {
  const nameSize = big ? "text-[13px]" : "text-[11px]";
  const timeSize = big ? "text-[11px]" : "text-[9px]";
  const badgeSize = big ? "text-[9px]" : "text-[8px]";
  return (
    <div className="mb-0.5 flex items-baseline gap-1.5">
      <span className={`${nameSize} font-medium text-stone-900`}>{name}</span>
      {app && (
        <span className={`rounded-sm bg-indigo-50 px-1 py-px ${badgeSize} font-medium tracking-wide text-indigo-700`}>
          APP
        </span>
      )}
      <span className={`${timeSize} text-stone-500`}>{time}</span>
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 py-2">
      <span className="boo-typing-dot h-1.5 w-1.5 rounded-full bg-stone-500" />
      <span className="boo-typing-dot h-1.5 w-1.5 rounded-full bg-stone-500" style={{ animationDelay: "200ms" }} />
      <span className="boo-typing-dot h-1.5 w-1.5 rounded-full bg-stone-500" style={{ animationDelay: "400ms" }} />
    </div>
  );
}
